import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Hexagon, Mail, ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      // Backend always returns ok (no enumeration). Show the confirmation pane.
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-9 h-9 rounded-xl bg-orange-500 grid place-items-center text-white">
            <Hexagon size={18} />
          </div>
          <div>
            <div className="text-base font-bold text-slate-900 tracking-tight">LeadHub</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Password reset</div>
          </div>
        </div>

        {sent ? (
          <div data-testid="forgot-password-sent" className="text-center py-4">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mb-3">
              <CheckCircle size={24} />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-1">Check your inbox</h1>
            <p className="text-sm text-slate-600 leading-relaxed mb-6">
              If an account exists for <b className="text-slate-900">{email}</b>, we&apos;ve emailed a password reset link.
              It expires in 1 hour. Don&apos;t forget to check spam.
            </p>
            <Link
              to="/login"
              data-testid="back-to-login"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700"
            >
              <ArrowLeft size={14} /> Back to login
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-password-form">
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">Forgot your password?</h1>
            <p className="text-sm text-slate-500 mt-1 mb-6">Enter the email you signed up with and we&apos;ll send you a reset link.</p>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Email</label>
            <div className="relative mb-5">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="forgot-password-email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full pl-9 pr-3 py-2.5 rounded-md border border-slate-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none text-sm"
              />
            </div>
            <button
              data-testid="forgot-password-submit"
              disabled={submitting}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-medium py-2.5 rounded-md transition-colors flex items-center justify-center gap-1.5 shadow-sm"
            >
              {submitting ? "Sending…" : "Send reset link"} {!submitting && <ArrowRight size={14} />}
            </button>
            <div className="text-center mt-5">
              <Link to="/login" data-testid="back-to-login" className="text-xs font-medium text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
                <ArrowLeft size={12} /> Back to login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
